/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ClipId, PlaylistClip } from "types/Clip";
import { BlockTypes } from "constants/BlockTypes";
import { findClipsAtTime, getPlaylistDuration } from "./PlaylistUtilities";
import { createLogger, EnhancedLogger } from "utilities/Logger";

/**
 * PlaylistManager
 *
 * Manages the sequencing and timing of audio clips in a playlist. The timeline maintains
 * the current state of playback, including which clips should be playing at any given time
 * and which clip should be considered the "foreground" clip (the main clip driving playback).
 *
 * Key Concepts:
 * - Foreground Clip: A transcription or non-background waveform clip that drives the main
 *   playback timing. Only one foreground clip can be active at a time.
 * - Timeline Time: The current playback position in milliseconds from the start of the playlist.
 * - Clip Transitions: Points where foreground clips change, requiring coordination of audio playback.
 *
 * Example Usage:
 * ```typescript
 * const timeline = new PlaylistManager();
 * timeline.updatePlaylist(myPlaylist);
 * timeline.updateTime(5000); // Move to 5 seconds into playlist
 * const currentClips = timeline.getCurrentClips();
 * ```
 */
export class PlaylistManager {
  private playlist: PlaylistClip[] = [];
  private currentTimeMs: number = 0;
  private duration: number = 0;
  private foregroundClipId?: ClipId;

  constructor(
    private readonly logger: EnhancedLogger = createLogger(PlaylistManager.name)
  ) {}

  updatePlaylist(playlist: PlaylistClip[]): void {
    const originalPlaylistDuration = getPlaylistDuration(this.playlist);
    const newPlaylistDuration = getPlaylistDuration(playlist);

    this.logger.log(
      `🐛 updatePlaylist BEFORE - playlist (${originalPlaylistDuration}ms):`,
      this.playlist
    );
    this.logger.log(
      `🦋 updatePlaylist AFTER - playlist (${newPlaylistDuration}ms):`,
      playlist
    );

    this.playlist = playlist;
    this.duration = newPlaylistDuration;

    // When playlist changes, we need to re-evaluate the foreground clip
    this.updateForegroundClip();
  }

  progressTime(newTime: number): void {
    if (newTime < this.currentTimeMs) {
      // Don't go back in time if overlapping tracks lag each other!
      this.logger.debug(
        `🛑⏪ progressTime - Ignoring attempt to go back in time from ${this.currentTimeMs}ms to ${newTime}ms.`
      );
      return;
    }

    this.setTime(newTime);
  }

  setTime(newTime: number): void {
    this.currentTimeMs = newTime;
    this.updateForegroundClip();
  }

  /**
   * If the ending clip is the current foreground clip, update the current foreground clip
   * to the next available foreground clip in the playlist. This is necessary to handle timing
   * gaps between foreground clips in the playlist.
   *
   * @param endingClipId - The ID of the clip that is ending.
   */
  handleClipMediaEnded(endedClipId: ClipId): void {
    const currentForegroundClip = this.getForegroundClip();

    if (currentForegroundClip?.id !== endedClipId) {
      // We only care about the current foreground clip ending
      return;
    }

    const newForegroundClip = this.playlist.find(
      (clip) =>
        (clip.type === BlockTypes.TRANSCRIPTION ||
          (clip.type === BlockTypes.WAVEFORM && !clip.isBackground)) &&
        clip.startTime > currentForegroundClip?.startTime // TODO: Bug - If fade overlaps completely with the ending clip, it will have same start time and be skipped.
    );

    this.logger.log(
      `⏭️ Progressing from ended clip ${currentForegroundClip.id} to next foreground clip: ${newForegroundClip?.id} starting at ${newForegroundClip?.startTime}, current time ${this.currentTimeMs}`
    );

    // If there is no new foreground clip then we want foreground clip to be set to undefined
    this.foregroundClipId = newForegroundClip?.id;
  }

  private updateForegroundClip(): void {
    // Find the first foreground clip at the current time
    const newForegroundClip = this.getCurrentClips().find(
      (clip) =>
        clip.type === BlockTypes.TRANSCRIPTION ||
        (clip.type === BlockTypes.WAVEFORM && !clip.isBackground)
    );

    const oldCurrentForegroundClip = this.getForegroundClip();

    if (oldCurrentForegroundClip?.id === newForegroundClip?.id) {
      // Don't bother if they're the same clip
      return;
    }

    const isCurrentClipStillCurrent = oldCurrentForegroundClip
      ? this.currentTimeMs >= oldCurrentForegroundClip.startTime &&
        this.currentTimeMs < oldCurrentForegroundClip.endTime
      : false;

    if (!isCurrentClipStillCurrent || newForegroundClip) {
      // If there is a new current foreground clip it should take precedence.
      // If there is no new current foreground clip then current will be set to undefined, which
      // is correct because the old current clip is no longer current either.
      this.logger.log(
        `🆙 Updating ${isCurrentClipStillCurrent ? "CURRENT" : "STALE"} foreground clip from ${this.foregroundClipId} to ${newForegroundClip?.id} @ current time ${this.currentTimeMs}`,
        newForegroundClip
      );
      this.foregroundClipId = newForegroundClip?.id;
    }
  }

  getCurrentClips(): PlaylistClip[] {
    return findClipsAtTime(this.playlist, this.currentTimeMs);
  }

  getForegroundClip(): PlaylistClip | undefined {
    if (!this.foregroundClipId) return undefined;
    return this.playlist.find((clip) => clip.id === this.foregroundClipId);
  }

  // Helper to check if we need to transition to next clip soon
  getTimeToNextClipTransition(): number | undefined {
    const current = this.getForegroundClip();
    if (!current) return undefined;

    const timeToEnd = current.endTime - this.currentTimeMs;
    if (timeToEnd < 100 && timeToEnd > 0) {
      return timeToEnd;
    }
  }

  getClip(clipId: ClipId): PlaylistClip | undefined {
    return this.playlist.find((clip) => clip.id === clipId);
  }

  getClipsWithAsset(assetId: string): PlaylistClip[] {
    return this.playlist.filter((clip) => clip.assetId === assetId);
  }

  getCurrentTimeMs(): number {
    return this.currentTimeMs;
  }

  getDurationMs(): number {
    return this.duration;
  }

  getClipsInRange(startTime: number, endTime: number): PlaylistClip[] {
    return this.playlist.filter(
      (clip) => clip.startTime < endTime && clip.endTime > startTime
    );
  }

  canHotSwapToNewPlaylist(
    newPlaylist: PlaylistClip[],
    compare: (oldClip: PlaylistClip, newClip: PlaylistClip) => boolean
  ): boolean {
    if (newPlaylist.length !== this.playlist.length) {
      return false;
    }

    return newPlaylist.every((newClip, idx): boolean => {
      const oldClip = this.playlist[idx];

      return compare(oldClip, newClip);
    });
  }
}
